import type { ActionState } from "../../shared/goals/actions";
import type { AD_BUTTONS, AdCard, AdFormat, AdPost } from "../../shared/goals/ready";
import type { RunState } from "../../shared/goals/runs";
import { post, request } from "./api";
import type { ConnectionCheck } from "./connectionsApi";

/** The words of an ad card: the agent's draft or the owner's edit. A card still drafting carries only `idea` (and `why` for a plan idea). */
export interface AdWords {
  name?: string;
  format?: AdFormat;
  chips?: string[];
  says?: string;
  primary?: string;
  headline?: string;
  cta?: keyof typeof AD_BUTTONS;
  idea?: string;
  why?: string;
  wants_script?: boolean;
  /** Photos people swipe: each card's headline and line, by card id. */
  cards?: AdCard[];
  /** Other words Meta can try, beside the ad's own. */
  alts?: { primary: string; headline: string }[];
  /** Other words the owner asked the agent for, still being written. */
  wants_alts?: number;
  /** Meta's label: made or edited with AI. */
  ai?: boolean;
  /** The published post the ad promotes. */
  post?: AdPost;
}

export type AdStatus = "drafting" | "needs_video" | "ready" | "launched";

export interface ReadyAd {
  id: string;
  ideaKey: string;
  origin: string;
  status: AdStatus;
  rev: number;
  /** The owner changed it; the agent no longer writes over it. */
  touched: boolean;
  words: AdWords;
  asset: { filename?: string; contentType?: string; size?: number } | null;
  /** Each card's photo, by card id, and the Stories and Reels version. */
  files: { cards: Record<string, { contentType: string | null }>; tall: { contentType: string | null } | null };
  /** Files the ad still needs before Meta can take it. */
  missing: number;
  providerAdId: string | null;
}

export interface ReadyEmail {
  step: number;
  subject: string;
  body: string;
  when: string;
  rev: number;
  approved: boolean;
  /** Given to Smartlead: it goes out as approved and can no longer change. */
  launched?: boolean;
}

/** One cold email list per market: the agent's list run and how many people it found. */
export interface ReadyList {
  market: string;
  state: RunState | "not_started";
  runId: string | null;
  people: number;
  withEmail: number;
  /** The clock this market's emails go out by: the owner's choice, its US state's, or the goal's. */
  sendTimezone: { timezone: string; source: "set" | "state" | "goal"; check: boolean };
}

/** The agent's latest drafting run for the ad cards or the emails. */
export interface DraftRun {
  id: string;
  state: RunState;
  error: string | null;
}

/** One market's Smartlead campaign and its latest send (the `email.step` action). */
export interface EmailSend {
  market: string;
  campaignState: "creating" | "unsure" | "ready" | "on" | "paused";
  lastSend: {
    id: string;
    state: ActionState;
    result: { error?: string; waiting?: string; added?: number; skipped?: { email: string; reason: string }[] } | null;
  } | null;
}

/** GET /goals/:id/ready. */
export interface ReadyView {
  ads: ReadyAd[];
  emails: ReadyEmail[];
  lists: ReadyList[];
  postalAddress: string | null;
  emailOn: boolean;
  metaOn: boolean;
  runs: { ads: DraftRun | null; emails: DraftRun | null };
  /** When and how fast cold email goes out; each market's clock is on its list. */
  sending: { days: ("sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat")[]; startHour: string; endHour: string; newPerDay: number; minTimeBetweenEmails: number } | null;
  /** What every email ends with, as it is sent: the postal address, then the unsubscribe line. */
  emailFooter: { postalAddress: string | null; unsubscribeText: string } | null;
  emailSends: EmailSend[];
}

/** A run that may still change: the agent has it, or will pick it up again. */
export const drafting = (r: DraftRun | null) => !!r && ["queued", "running", "waiting", "interrupted"].includes(r.state);
/** A run that ended without its result. */
export const stopped = (r: DraftRun | null) => !!r && ["failed", "cancelled"].includes(r.state);

export type CampaignState = "creating" | "unsure" | "paused" | "on";

/** One of the goal's latest campaign actions, with what came of it. */
export interface CampaignAction {
  id: string;
  kind: string;
  state: ActionState;
  result: { error?: string; waiting?: string; adopted?: boolean; already?: boolean; on?: boolean; dailyCents?: number; spendCapCents?: number } | null;
  /** Which of the goal's rules held a refused action back. */
  reason?: string | null;
  not_before: string | null;
  updated_at: string;
}

/** GET /goals/:id/launch: the checks, the campaign as it stands, and the latest campaign actions. */
export interface LaunchView {
  metaOn: boolean;
  checks: ConnectionCheck[];
  ready: boolean;
  campaign: { state: CampaignState; campaignId: string | null; adSetId?: string | null; dailyCents: number | null; settleUntil: string | null } | null;
  actions: CampaignAction[];
}

const goal = (id: string) => `/goals/${encodeURIComponent(id)}`;
const ad = (id: string, adId: string) => `${goal(id)}/ads/${encodeURIComponent(adId)}`;

export const readyApi = {
  get: (id: string) => request<ReadyView>(`${goal(id)}/ready`),
  addIdea: (id: string, idea: string, format: AdFormat) => post<{ ad: ReadyAd }>(`${goal(id)}/ads`, { idea, format }),
  /** One of the plan's ideas becomes an ad. */
  usePlanIdea: (id: string, key: string) => post<{ ad: ReadyAd }>(`${goal(id)}/ads/plan`, { key }),
  /** A video or photo the owner already has becomes an ad; `note` says what it shows. */
  addVideo: (id: string, file: File, note: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("note", note);
    return request<{ ad: ReadyAd }>(`${goal(id)}/ads/video`, { method: "POST", body: form });
  },
  /** A script to read, for an ad of the owner on camera. */
  askScript: (id: string, adId: string) => post<{ ad: ReadyAd }>(`${ad(id, adId)}/script`),
  editAd: (id: string, adId: string, rev: number, words: Partial<AdWords>) =>
    request<{ ad: ReadyAd }>(ad(id, adId), { method: "PATCH", body: JSON.stringify({ rev, words }) }),
  /** A file for the ad: its video or photo ("main"), a card's photo ("card:<id>"), or the Stories and Reels version ("tall"). */
  upload: (id: string, adId: string, rev: number, file: File, slot = "main") => {
    const form = new FormData();
    form.append("file", file);
    form.append("rev", String(rev));
    form.append("slot", slot);
    return request<{ ad: ReadyAd }>(`${ad(id, adId)}/video`, { method: "POST", body: form });
  },
  removeTall: (id: string, adId: string, rev: number) => request<{ ad: ReadyAd }>(`${ad(id, adId)}/video?slot=tall&rev=${rev}`, { method: "DELETE" }),
  /** One of the ad's files, as the page streams it. The revision keeps a replaced file from showing from cache. */
  videoUrl: (id: string, adId: string, rev: number, slot = "main") => `/api${ad(id, adId)}/video?v=${rev}${slot === "main" ? "" : `&slot=${encodeURIComponent(slot)}`}`,
  /** Two to ten photos the owner already has, as one ad people swipe through; `note` says what they show. */
  addPhotos: (id: string, files: File[], note: string) => {
    const form = new FormData();
    for (const f of files) form.append("files", f);
    form.append("note", note);
    return request<{ ad: ReadyAd }>(`${goal(id)}/ads/photos`, { method: "POST", body: form });
  },
  /** The latest posts on the business's Facebook Page. */
  pagePosts: (id: string) => request<{ posts: AdPost[] }>(`${goal(id)}/page-posts`),
  addPost: (id: string, postId: string) => post<{ ad: ReadyAd }>(`${goal(id)}/ads/post`, { postId }),
  setPost: (id: string, adId: string, rev: number, postId: string) => request<{ ad: ReadyAd }>(`${ad(id, adId)}/post`, { method: "PUT", body: JSON.stringify({ rev, postId }) }),
  /** One more set of other words Meta can try; the agent writes them. */
  askAlts: (id: string, adId: string) => post<{ ad: ReadyAd }>(`${ad(id, adId)}/alts`),
  removeIdea: (id: string, adId: string) => request<{ ok: true }>(ad(id, adId), { method: "DELETE" }),
  /** Put back an idea the owner removed, as it was. */
  restoreIdea: (id: string, adId: string) => post<{ ok: true }>(`${ad(id, adId)}/restore`),
  /** Ask the agent to draft the ad cards still waiting for their words, or the emails, again. */
  draftAgain: (id: string, what: "ads" | "emails") => post<{ run: { id: string; state: RunState } | null }>(`${goal(id)}/${what}/draft`),
  /** Build a market's list again after its run stopped. */
  retryList: (id: string, market: string) => post<{ runId: string }>(`${goal(id)}/lists/${encodeURIComponent(market)}/retry`),
  editEmail: (id: string, e: ReadyEmail) =>
    request<ReadyView>(`${goal(id)}/emails/${e.step}`, { method: "PATCH", body: JSON.stringify({ rev: e.rev, subject: e.subject, body: e.body, when: e.when }) }),
  approveEmails: (id: string, revs: Record<string, number>) => post<ReadyView>(`${goal(id)}/emails/approve`, { revs }),
  unapproveEmails: (id: string) => post<ReadyView>(`${goal(id)}/emails/unapprove`),
  postalAddress: (id: string, address: string) => request<ReadyView>(`${goal(id)}/postal-address`, { method: "PUT", body: JSON.stringify({ address }) }),
  launch: (id: string) => request<LaunchView>(`${goal(id)}/launch`),
  /** Every check run against Meta now. */
  launchCheck: (id: string) => post<LaunchView>(`${goal(id)}/launch/check`),
  /** Launch: approve what the page showed (the goal's version and each ad's revision), create the campaign and switch it on. */
  launchNow: (id: string, seen: { version: number; ads: Record<string, number> }) => post<LaunchView & { actionId: string; state: ActionState }>(`${goal(id)}/launch`, seen),
  turnOn: (id: string) =>
    post<LaunchView & { outcome: string | null; state: ActionState; result: { error?: string; waiting?: string } | null }>(`${goal(id)}/turn-on`),
};
